import { Injectable } from '@angular/core';
import moment from 'moment-timezone';
import { TimeFrame } from '../interfaces/time-frame.interface';
import { Order } from '../interfaces/order.interface';
import { HandoffType } from '../interfaces/handoff-type.enum';

@Injectable({
  providedIn: 'root',
})
export class DateTimeService {
  order: Order;
  days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  getDates(): { key: string; value: TimeFrame }[] {
    const today = moment();
    const newArr: { key: string; value: TimeFrame }[] = [];
    let pickupHours: TimeFrame[];

    switch (this.order.handoffType) {
      case HandoffType.pickup:
        pickupHours = this.order?.location?.pickupHours;
        break;
      case HandoffType.delivery:
        pickupHours = this.order?.location?.deliveryHours;
        break;
      case HandoffType.dispatch:
        pickupHours = this.order?.location?.dispatchHours;
        break;
      case HandoffType.curbside:
        pickupHours = this.order?.location?.curbsideHours;
        break;
      case HandoffType.dineIn:
        pickupHours = this.order?.location?.tablesideHours;
        break;
      case HandoffType.driveThru:
        pickupHours = this.order?.location?.driveThruHours;
        break;
      default:
        pickupHours = this.order?.location?.pickupHours;
        break;
    }

    newArr.push({
      key: 'Select a date',
      value: null,
    });

    if (pickupHours && pickupHours.length) {
      pickupHours.forEach(c => {
        const start = moment(c.start);
        if (start.isSameOrAfter(today, 'day')) {
          let suffix;
          if (start.isSame(today, 'day') && moment(today).isSameOrBefore(moment(c.end).subtract(15, 'minute'), 'minute')) {
            suffix = ' (Today)';
          } else if (moment(start).isSame(moment(today).add(1, 'day'), 'day')) {
            suffix = ' (Tomorrow)';
          } else {
            suffix = start.format(' (dddd)');
          }
          if (moment(today).isSameOrBefore(moment(c.end), 'minute')) {
            newArr.push({
              key: start.format('M/D') + suffix,
              value: c,
            });
          }
        }
      });
    }

    const todayTimeFrame = newArr.find(day => day.key.includes(' (Today)'));
    if (todayTimeFrame) {
      const index = newArr.indexOf(todayTimeFrame);
      if (moment().isAfter(moment(todayTimeFrame.value.end), 'minute')) {
        if (index > -1) {
          newArr.splice(index, 1);
        }
      }
    }

    return newArr;
  }

  getTimes(range): { key: string; value: string }[] {
    const newArr: { key: string; value: string }[] = [];

    if (range) {
      let startTime = moment(range.start);

      newArr.push({
        key: 'Select a time',
        value: '',
      });

      if (moment().isSame(startTime, 'day')) {
        startTime = this.roundTimeToNearest(moment(this.order.earliestReadyTimestamp), 15);
      }

      const endTime = moment(range.end, 'YYYYMMDD HH:mm');
      const timeToAdd = startTime.clone();

      while (timeToAdd.isBefore(endTime, 'minute')) {
        newArr.push({
          key: timeToAdd.format('h:mm A'),
          value: timeToAdd.toDate().toISOString(),
        });
        timeToAdd.add(15, 'minutes');
      }
    }

    return newArr;
  }

  getTimesToday(): { key: string; value: string }[] {
    const newArr: { key: string; value: string }[] = [];
    let startTime = moment();

    newArr.push({
      key: 'Select a time',
      value: '',
    });

    if (moment().isSame(startTime, 'day')) {
      startTime = this.roundTimeToNearest(moment(), 15);
    }

    const tonight = moment().endOf('day');
    const timeToAdd = startTime.clone();

    while (timeToAdd.isBefore(tonight, 'minute')) {
      newArr.push({
        key: timeToAdd.format('h:mm A'),
        value: timeToAdd.toDate().toISOString(),
      });
      timeToAdd.add(15, 'minutes');
    }

    return newArr;
  }

  /**
   * Get available group order times using simple local time calculation
   * The display timezone will be handled by the Angular date pipe with utcOffset
   * @param locationTimezone - The timezone of the order location (optional, not used in this approach)
   */
  getGroupOrderTimesToday(locationTimezone?: string): { key: string; value: string }[] {
    const newArr: { key: string; value: string }[] = [];

    // Define the time intervals in minutes and their respective labels
    const timeIntervals = [
      { minutes: 30, label: '30 minutes' },
      { minutes: 60, label: '60 minutes' },
      { minutes: 90, label: '90 minutes' },
    ];

    timeIntervals.forEach(interval => {
      // Create deadline in local time - the Angular date pipe will handle timezone display
      const currentTime = moment();
      const deadlineTime = currentTime.clone().add(interval.minutes, 'minutes');

      newArr.push({
        key: interval.label,
        value: deadlineTime.toISOString(),
      });
    });

    return newArr;
  }

  /**
   * Rounds a moment up to the next nearest interval of the specified base
   * @param momentObj - The moment object to round
   * @param base - Number which minutes will be rounded to (e.g. 15, 30)
   */
  private roundTimeToNearest(momentObj: moment.Moment, base: number): moment.Moment {
    const m = momentObj.clone();
    const remainder = m.minute() % base;
    if (remainder === 0) {
      return m;
    }
    return m.add(base - remainder, 'minutes');
  }

  /**
   * Helper method to get current time in a specific timezone
   * @param timezone - The timezone to get current time for
   */
  getCurrentTimeInTimezone(timezone: string): moment.Moment {
    return moment().tz(timezone);
  }

  /**
   * Helper method to convert a time to a specific timezone
   * @param time - The time to convert (Date, moment, or ISO string)
   * @param timezone - The target timezone
   */
  convertToTimezone(time: Date | moment.Moment | string, timezone: string): moment.Moment {
    return moment(time).tz(timezone);
  }

  isToday(date) {
    return moment(date).isSame(moment(), 'day');
  }
}
